import { FirstPageLegalNotice } from 'components/FirstPageLegalNotice/FirstPageLegalNotice'
import React, { useEffect, useMemo, type PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/shallow'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandStore } from 'zustandGlobalStore'
import { DYNAMIC_COMPONENTS_MAP } from 'constants/dynamic-components-map'
import { ID_TO_PAGE_CONFIG_MAP } from 'constants/pages-configs-map'
import type { Cohort } from 'root-constants'
import { FlowRouterContext } from 'contexts/flowRouterContext'
import { useCreateXStateMachine } from 'hooks/useCreateXStateMachine'
import { useStartSessionEvent } from 'hooks/useStartSessionEvent'
import { useUpdateUrlWithRequiredSearchParams } from 'hooks/useUpdateUrlWithRequiredSearchParams'
import { useEventLoggerInitialization } from 'hooks/useEventLoggerInitialization'
import { useFacebookPixelInitialization } from 'hooks/useFacebookPixelInitialization'
import { useInitPinterestTag } from 'hooks/useInitPinterestTag'
import { useGAPageTracker } from 'hooks/useGAPageTracker'
import { useManualPageViewTracker } from 'hooks/useManualPageViewTracker'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

const FallbackComponent: React.FC<PropsWithChildren> = ({ children }) => (
  <div>{children}</div>
)

export const FlowRouter: React.FC = () => {
  const { i18n } = useTranslation()
  const { cohort, currentPageIndex, currentPageId, leadId } = useZustandStore(
    useShallow((state) => ({
      cohort: state.lead?.cohort || null,
      leadId: state.lead?.id,
      pinterestTagId: state.lead?.config?.cohort?.pinterestTagId || '',
      currentPageIndex: state.currentPageIndex,
      currentPageId: state.currentPageId,
    })),
  )

  const currentPageConfig = currentPageId
    ? ID_TO_PAGE_CONFIG_MAP[currentPageId]
    : null
  const { language } = useZustandSessionStore(
    useShallow((state) => ({
      language: state.language,
    })),
  )

  const { RoutesList, Header, BgPreloadingMediaProvider } = useMemo(
    () => ({
      RoutesList:
        DYNAMIC_COMPONENTS_MAP?.[cohort as Cohort]?.RoutesList ||
        FallbackComponent,
      Header:
        DYNAMIC_COMPONENTS_MAP?.[cohort as Cohort]?.Header || FallbackComponent,
      BgPreloadingMediaProvider:
        DYNAMIC_COMPONENTS_MAP?.[cohort as Cohort]?.BgPreloadingMediaProvider ||
        FallbackComponent,
    }),
    [cohort],
  )

  const { flowRouterContext } = useCreateXStateMachine()

  useInitPinterestTag()
  useFacebookPixelInitialization()
  useGAPageTracker()
  useManualPageViewTracker()
  useEventLoggerInitialization()
  useStartSessionEvent()
  useUpdateUrlWithRequiredSearchParams()

  useEffect(() => {
    if (currentPageIndex === 1) {
      window.fbq?.('track', 'CustomizeProduct', {}, { eventID: leadId })
      googleAnalyticsLogger.logFirstPageCompleted()
    }
  }, [currentPageIndex, leadId])

  useEffect(() => {
    i18n.changeLanguage(language)
  }, [i18n, language])

  return (
    <BgPreloadingMediaProvider>
      <FlowRouterContext.Provider value={flowRouterContext}>
        <Header />
        <RoutesList />
        {currentPageConfig?.legalNotice === 'fixed' && <FirstPageLegalNotice />}
      </FlowRouterContext.Provider>
    </BgPreloadingMediaProvider>
  )
}
